import { ORPCError } from "@orpc/client";
import type { EarlyAccessFeature, FeedbackReportType } from "@rosc/contracts";
import { referenceClient } from "@/lib/orpc/client";

export const FEEDBACK_TYPES = [
  "bug",
  "idea",
  "note_quality",
  "other",
] as const satisfies readonly FeedbackReportType[];
export type FeedbackType = (typeof FEEDBACK_TYPES)[number];
export const FEEDBACK_MESSAGE_MAX = 4000;

// The web build that sends a report: the release SHA in deployed builds,
// "dev" locally.
declare const __ROSC_WEB_BUILD__: string | undefined;
export const webBuild = (): string => {
  const value =
    typeof __ROSC_WEB_BUILD__ === "string" ? __ROSC_WEB_BUILD__ : "dev";
  return /^[A-Za-z0-9._@+-]{1,64}$/.test(value) ? value : "dev";
};

export interface FeedbackDraft {
  type: FeedbackType;
  message: string;
  // Sent only when the person keeps "include this visit" on. Ids only, never
  // note or transcript text.
  visitPublicId?: string | null;
  // "Send feedback on this beta": the early-access feature it is about.
  featureKey?: EarlyAccessFeature;
}

export type FeedbackSendError = "rate_limited" | "unavailable" | "failed";

export const feedbackSendError = (error: unknown): FeedbackSendError =>
  error instanceof ORPCError
    ? error.code === "TOO_MANY_REQUESTS"
      ? "rate_limited"
      : error.code === "SERVICE_UNAVAILABLE"
        ? "unavailable"
        : "failed"
    : "failed";

export const sendFeedbackReport = async (
  draft: FeedbackDraft,
  expectedUserUid?: string,
): Promise<void> => {
  await referenceClient.submitFeedbackReport(
    {
      body: {
        type: draft.type,
        message: draft.message.trim(),
        ...(draft.visitPublicId ? { visitPublicId: draft.visitPublicId } : {}),
        ...(draft.featureKey ? { featureKey: draft.featureKey } : {}),
        appVersion: webBuild(),
      },
    },
    { context: { expectedUserUid } },
  );
};
